import React, { useState, useEffect } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import CircularProgress from "@mui/material/CircularProgress";
import { postRightworkAPI } from "../requests/utilities/requests";
import { API_ops_assistant_confirm_action } from "../../../constants";
import { toast } from "react-toastify";

const TYPE_OPTIONS = [
  { value: "PTO", label: "PTO", gate: "can_request_pto" },
  { value: "SICK", label: "Sick", gate: "can_request_sicktime" },
  { value: "UNPAID", label: "Unpaid" },
];

// Quarter-hour increments for the partial-day pickers (matches the
// scheduling UI's granularity — 0.0 → 23.75 in 0.25 steps).
const QHNUM_OPTIONS = (() => {
  const out = [];
  for (let q = 0; q < 24 * 4; q++) {
    out.push(q / 4);
  }
  return out;
})();

function fmtQhnum(qhnum) {
  if (qhnum === null || qhnum === undefined) return "";
  const hour = Math.floor(qhnum) % 24;
  const minute = Math.round((qhnum - Math.floor(qhnum)) * 60);
  const period = hour < 12 ? "AM" : "PM";
  let displayHour = hour > 12 ? hour - 12 : hour;
  if (displayHour === 0) displayHour = 12;
  return `${displayHour}:${String(minute).padStart(2, "0")} ${period}`;
}

export default function TimeOffProposalCard({
  proposal,
  sessionUuid,
  homeStore,
  onStatusChange,
}) {
  const summary = proposal.summary || {};

  const [status, setStatus] = useState(proposal.status || "pending");
  const [loading, setLoading] = useState(false);

  // Per-employee leave gates from the proposal summary. Default to
  // true so a missing flag (legacy proposal, summary never set
  // them) degrades to the existing UI's permissive behavior. The
  // backend helper enforces the same gates regardless of what the
  // card sends, so this is purely UX filtering.
  const canRequestPto = summary.can_request_pto !== false;
  const canRequestSicktime = summary.can_request_sicktime !== false;
  const visibleTypeOptions = TYPE_OPTIONS.filter((opt) => {
    if (opt.gate === "can_request_pto" && !canRequestPto) return false;
    if (opt.gate === "can_request_sicktime" && !canRequestSicktime) return false;
    return true;
  });

  const [entryTitle, setEntryTitle] = useState(summary.entry_title || "");
  const [dateLower, setDateLower] = useState(summary.date_lower_bound || "");
  const [dateUpper, setDateUpper] = useState(summary.date_upper_bound || "");
  // If the LLM proposed a type that's now disallowed for this
  // employee (resolution should have rejected, but in case of stale
  // proposal or relaxed flags), downshift to the first visible
  // option so the dropdown isn't stuck at an invalid value.
  const initialType = (() => {
    const proposed = summary.time_off_type || "PTO";
    const stillAllowed = visibleTypeOptions.some((opt) => opt.value === proposed);
    if (stillAllowed) return proposed;
    return visibleTypeOptions[0]?.value || "UNPAID";
  })();
  const [timeOffType, setTimeOffType] = useState(initialType);

  const initialPartialDay =
    summary.start_qhnum !== null && summary.start_qhnum !== undefined;
  const [partialDay, setPartialDay] = useState(initialPartialDay);
  const [startQhnum, setStartQhnum] = useState(
    summary.start_qhnum !== null && summary.start_qhnum !== undefined
      ? summary.start_qhnum
      : 9.0
  );
  const [endQhnum, setEndQhnum] = useState(
    summary.end_qhnum !== null && summary.end_qhnum !== undefined
      ? summary.end_qhnum
      : 17.0
  );

  useEffect(() => {
    if (
      (proposal.status === "cancelled" || proposal.status === "rejected") &&
      status === "pending"
    ) {
      setStatus("cancelled");
    }
  }, [proposal.status]);

  // Partial-day requires single-day. If the manager toggles to a date
  // range, hide and disable the partial-day controls so the executor
  // never sees a conflicting payload.
  const isRange = !!dateUpper && dateUpper !== dateLower;
  const partialDayEffective = partialDay && !isRange;

  const datesOutOfOrder =
    dateLower && dateUpper && dateUpper < dateLower;
  const partialDayInvalid = partialDayEffective && startQhnum >= endQhnum;
  const confirmDisabled =
    loading ||
    !dateLower ||
    datesOutOfOrder ||
    !timeOffType ||
    partialDayInvalid;

  const handleAction = async (action) => {
    setLoading(true);
    try {
      const payload = {
        store_id: homeStore,
        session_uuid: sessionUuid,
        proposal_id: proposal.proposal_id,
        action,
      };

      if (action === "confirm") {
        // Send the full edited form back as overrides. confirmation.py
        // merges these into tool_args before the executor runs.
        const overrides = {
          title: entryTitle.trim(),
          date_lower_bound: dateLower,
          // Empty string clears upper bound to make the entry single-day.
          date_upper_bound: isRange ? dateUpper : "",
          time_off_type: timeOffType,
        };
        if (partialDayEffective) {
          overrides.start_qhnum = startQhnum;
          overrides.end_qhnum = endQhnum;
        } else {
          // Explicit clear so a previously-set partial-day override
          // doesn't linger on the proposal if the manager toggled
          // back to all-day.
          overrides.start_qhnum = null;
          overrides.end_qhnum = null;
        }
        payload.overrides = overrides;
      }

      const res = await postRightworkAPI(
        API_ops_assistant_confirm_action,
        payload
      );
      if (res.status === 200) {
        const newStatus = action === "confirm" ? "confirmed" : "cancelled";
        setStatus(newStatus);
        onStatusChange?.(proposal.proposal_id, newStatus);
      } else {
        toast.error("Failed to add time off.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.system?.message ||
          err?.response?.data?.error ||
          "Failed to add time off."
      );
    }
    setLoading(false);
  };

  if (status === "confirmed") {
    const summaryDates = isRange
      ? `${dateLower} — ${dateUpper}`
      : partialDayEffective
      ? `${dateLower} · ${fmtQhnum(startQhnum)} — ${fmtQhnum(endQhnum)}`
      : dateLower;
    return (
      <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700 my-2">
        <CheckCircleIcon
          fontSize="small"
          className="text-green-600 mt-0.5 shrink-0"
        />
        <div className="min-w-0">
          <div className="font-medium">
            Added {timeOffType === "SICK" ? "sick leave" : timeOffType.toLowerCase() + " time off"} for {summary.employee_name}
          </div>
          <div className="text-xs opacity-80 truncate">{summaryDates}</div>
        </div>
      </div>
    );
  }

  if (status === "cancelled" || status === "rejected") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-500 my-2">
        <CancelIcon fontSize="small" />
        <span>Cancelled</span>
      </div>
    );
  }

  return (
    <div className="my-2 max-w-md rounded-lg border border-violet-200 bg-violet-50">
      <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 border-b border-violet-200 rounded-t-lg">
        <EventBusyIcon fontSize="small" className="text-violet-700" />
        <span className="text-sm font-medium text-violet-800">
          Add Time Off
          {summary.employee_name && (
            <span className="text-violet-700 font-normal">
              {" — "}
              {summary.employee_name}
            </span>
          )}
        </span>
      </div>

      <div className="px-3 py-2 space-y-2">
        {/* Title */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-0.5">
            Title <span className="text-gray-400">(optional)</span>
          </label>
          <input
            type="text"
            value={entryTitle}
            onChange={(e) => setEntryTitle(e.target.value)}
            maxLength={200}
            className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white"
            placeholder="e.g. Camping, Doctor appointment"
          />
        </div>

        {/* Dates */}
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-0.5">
              Start date
            </label>
            <input
              type="date"
              value={dateLower}
              onChange={(e) => setDateLower(e.target.value)}
              className="w-full px-3 py-2 text-[13px] bg-white border border-[#bdbdbd] rounded-md hover:border-slate-900 focus:outline-none focus:border-violet-700 text-slate-900"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-0.5">
              End date <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="date"
              value={dateUpper}
              onChange={(e) => setDateUpper(e.target.value)}
              className="w-full px-3 py-2 text-[13px] bg-white border border-[#bdbdbd] rounded-md hover:border-slate-900 focus:outline-none focus:border-violet-700 text-slate-900"
            />
          </div>
        </div>
        {datesOutOfOrder && (
          <div className="text-[11px] text-red-500">
            End date must be on or after start date.
          </div>
        )}

        {/* Type */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-0.5">
            Type
          </label>
          <select
            value={timeOffType}
            onChange={(e) => setTimeOffType(e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white"
          >
            {visibleTypeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {(!canRequestPto || !canRequestSicktime) && (
            <div className="text-[11px] text-gray-500 mt-0.5">
              {!canRequestPto && !canRequestSicktime
                ? "PTO and Sick aren't enabled for this employee."
                : !canRequestPto
                ? "PTO isn't enabled for this employee."
                : "Sick isn't enabled for this employee."}
            </div>
          )}
        </div>

        {/* Partial day — single-day only */}
        <div>
          <label className="flex items-center gap-1.5 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={partialDay}
              onChange={(e) => setPartialDay(e.target.checked)}
              disabled={isRange}
              className="accent-violet-700"
            />
            Partial day
            {isRange && (
              <span className="text-[11px] text-gray-400">
                (single-day only)
              </span>
            )}
          </label>
          {partialDayEffective && (
            <div className="flex gap-2 mt-1">
              <select
                value={startQhnum}
                onChange={(e) => setStartQhnum(Number(e.target.value))}
                className={`flex-1 text-sm border rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white ${
                  partialDayInvalid ? "border-red-400" : "border-gray-300"
                }`}
              >
                {QHNUM_OPTIONS.map((q) => (
                  <option key={`s-${q}`} value={q}>
                    {fmtQhnum(q)}
                  </option>
                ))}
              </select>
              <span className="self-center text-xs text-gray-500">to</span>
              <select
                value={endQhnum}
                onChange={(e) => setEndQhnum(Number(e.target.value))}
                className={`flex-1 text-sm border rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white ${
                  partialDayInvalid ? "border-red-400" : "border-gray-300"
                }`}
              >
                {QHNUM_OPTIONS.map((q) => (
                  <option key={`e-${q}`} value={q}>
                    {fmtQhnum(q)}
                  </option>
                ))}
              </select>
            </div>
          )}
          {partialDayInvalid && (
            <div className="text-[11px] text-red-500 mt-0.5">
              Start time must be before end time.
            </div>
          )}
        </div>

      </div>

      <div className="flex justify-end gap-2 px-3 py-2 border-t border-violet-200 bg-white rounded-b-lg">
        <button
          onClick={() => handleAction("reject")}
          disabled={loading}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={() => handleAction("confirm")}
          disabled={confirmDisabled}
          className="px-3 py-1.5 text-sm rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50 flex items-center gap-1"
        >
          {loading && <CircularProgress size={14} sx={{ color: "white" }} />}
          Add time off
        </button>
      </div>
    </div>
  );
}
